import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FiUser, FiClock } from 'react-icons/fi';
import { FaArrowLeft, FaComment, FaTag, FaCheckCircle } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faSave } from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from '../services/apiService';

interface LocationState {
  feedback?: any;
}

const FeedbackDetails: React.FC = () => {
  const { feedbackId } = useParams<{ feedbackId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;

  const [feedback, setFeedback] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const statusOptions = ['new', 'pending', 'in_progress', 'reviewing', 'resolved', 'closed', 'rejected', 'dismissed'];

  useEffect(() => {
    if (locationState?.feedback) {
      setFeedback(locationState.feedback);
      setSelectedStatus(locationState.feedback.status || '');
    } else if (feedbackId) {
      loadFeedbackDetails();
    }
  }, [feedbackId, locationState]);

  const loadFeedbackDetails = async () => {
    if (!feedbackId) return;
    try {
      setLoading(true);
      const data = await apiService.getFeedbackById(feedbackId);
      setFeedback(data);
      setSelectedStatus(data.status || '');
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch feedback details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!feedbackId || !selectedStatus || selectedStatus === feedback?.status) return;

    try {
      setUpdatingStatus(true);
      await apiService.updateFeedbackStatus(feedbackId, selectedStatus);
      setFeedback((prev: any) => ({ ...prev, status: selectedStatus }));
      toast.success('Status updated successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update status');
      console.error(err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = (status: string) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'new' || statusLower === 'pending') return 'bg-blue-100 text-blue-800 border-blue-300';
    if (statusLower === 'in_progress' || statusLower === 'in-progress' || statusLower === 'reviewing') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (statusLower === 'resolved' || statusLower === 'completed' || statusLower === 'closed') return 'bg-green-100 text-green-800 border-green-300';
    if (statusLower === 'rejected' || statusLower === 'dismissed') return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const getTypeColor = (type: string) => {
    const typeLower = (type || '').toLowerCase();
    if (typeLower === 'bug' || typeLower === 'issue') return 'bg-red-100 text-red-800';
    if (typeLower === 'feature' || typeLower === 'suggestion') return 'bg-purple-100 text-purple-800';
    if (typeLower === 'improvement') return 'bg-blue-100 text-blue-800';
    if (typeLower === 'question') return 'bg-cyan-100 text-cyan-800';
    return 'bg-gray-100 text-gray-600';
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const formatLabel = (key: string) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^\w/, (c) => c.toUpperCase())
      .trim();
  };

  const renderValue = (key: string, value: any): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">N/A</span>;
    }

    if (typeof value === 'boolean') {
      return (
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }

    if (key.toLowerCase() === 'status') {
      const statusColor = getStatusColor(value);
      return (
        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${statusColor}`}>
          {value}
        </span>
      );
    }

    if (key.toLowerCase() === 'type' || key.toLowerCase() === 'category') {
      const typeColor = getTypeColor(value);
      return (
        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${typeColor}`}>
          {value}
        </span>
      );
    }

    if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time') || key.toLowerCase().includes('createdat') || key.toLowerCase().includes('updatedat')) {
      return formatDate(value);
    }

    if (typeof value === 'object') {
      return (
        <pre className="text-xs bg-gray-50 dark:bg-dark-500 p-2 rounded overflow-x-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    return String(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading feedback details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <FaArrowLeft className="mr-2 h-4 w-4" />
          Back
        </button>
        <div className="bg-red-50 dark:bg-danger-500/20 border border-red-200 dark:border-danger-500/30 rounded-lg p-4">
          <p className="text-red-800 dark:text-danger-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <FaArrowLeft className="mr-2 h-4 w-4" />
          Back
        </button>
        <div className="text-center py-12">
          <FaComment className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Feedback not found</p>
        </div>
      </div>
    );
  }

  // Separate fields into categories
  const excludeFromBasic = ['message', 'content', 'description', 'feedback', 'userInfo', 'user_info', 'metadata', 'attachments', '__v'];
  const basicFields = Object.entries(feedback).filter(([key]) => !excludeFromBasic.some(e => key.toLowerCase().includes(e.toLowerCase())));
  const messageField = Object.entries(feedback).find(([key]) => ['message', 'content', 'description', 'feedback'].includes(key.toLowerCase()));

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-gray-100 dark:bg-dark-400/60 hover:bg-gray-200 dark:hover:bg-dark-400/80 transition-colors"
          >
            <FaArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Feedback Details</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 font-mono">
              {feedback._id || feedback.id}
            </p>
          </div>
        </div>
        <button
          onClick={loadFeedbackDetails}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-dark-300/40 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400/60 hover:bg-gray-50 dark:hover:bg-dark-400/80 disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faRefresh} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Status Update Card */}
      <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <FaCheckCircle className="mr-2 h-5 w-5 text-blue-500" />
          Update Status
        </h3>
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-xs">
            <label htmlFor="status-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Current Status
            </label>
            <select
              id="status-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select status</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>{status.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div className="pt-6">
            <button
              onClick={handleStatusUpdate}
              disabled={updatingStatus || !selectedStatus || selectedStatus === feedback.status}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FontAwesomeIcon icon={faSave} className={`mr-2 ${updatingStatus ? 'animate-spin' : ''}`} />
              {updatingStatus ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information Card */}
        <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FaTag className="mr-2 h-5 w-5 text-blue-500" />
            Basic Information
          </h3>
          <div className="space-y-4">
            {basicFields.map(([key, value]) => (
              <div key={key} className="flex justify-between items-start py-2 border-b border-gray-100 dark:border-dark-300/20 last:border-0">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{formatLabel(key)}</span>
                <span className="text-sm text-gray-900 dark:text-white text-right max-w-[60%]">
                  {renderValue(key, value)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* User Information Card */}
        {(feedback.userInfo || feedback.user_info || feedback.user) && (
          <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <FiUser className="mr-2 h-5 w-5 text-purple-500" />
              User Information
            </h3>
            <div className="space-y-4">
              {Object.entries(feedback.userInfo || feedback.user_info || feedback.user || {}).map(([key, value]) => (
                <div key={key} className="flex justify-between items-start py-2 border-b border-gray-100 dark:border-dark-300/20 last:border-0">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{formatLabel(key)}</span>
                  <span className="text-sm text-gray-900 dark:text-white text-right max-w-[60%]">
                    {renderValue(key, value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Message/Content Card */}
      {messageField && (
        <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FaComment className="mr-2 h-5 w-5 text-green-500" />
            {formatLabel(messageField[0])}
          </h3>
          <div className="bg-gray-50 dark:bg-dark-500 rounded-lg p-4">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {messageField[1] as string || 'No content'}
            </p>
          </div>
        </div>
      )}

      {/* Metadata Card */}
      {(feedback.metadata || feedback.attachments) && (
        <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FiClock className="mr-2 h-5 w-5 text-orange-500" />
            Additional Information
          </h3>
          <pre className="text-xs bg-gray-50 dark:bg-dark-500 p-4 rounded-lg overflow-x-auto text-gray-700 dark:text-gray-300">
            {JSON.stringify(feedback.metadata || feedback.attachments, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default FeedbackDetails;
